import { useCallback } from 'react'
import { useLocation, useHistory } from 'react-router-dom'

/**
 * Hook for working with URL search parameters (React Router v5)
 */
export function useSearchParam() {
  const location = useLocation()
  const history = useHistory()

  const get = useCallback(
    (key) => {
      const params = new URLSearchParams(location.search)
      return params.get(key)
    },
    [location.search]
  )

  const set = useCallback(
    (key, value) => {
      const params = new URLSearchParams(location.search)
      params.set(key, value)
      history.replace({
        pathname: location.pathname,
        search: params.toString(),
      })
    },
    [location, history]
  )

  const remove = useCallback(
    (key) => {
      const params = new URLSearchParams(location.search)
      params.delete(key)
      history.replace({
        pathname: location.pathname,
        search: params.toString(),
      })
    },
    [location, history]
  )

  const setMany = useCallback((entriesObject) => {
    const params = new URLSearchParams(location.search)

    Object.entries(entriesObject || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, String(value))
      } else {
        params.delete(key)
      }
    })

    history.replace({
      pathname: location.pathname,
      search: params.toString(),
    })
  }, [location, history])

  const setAll = useCallback(
    (entriesObject) => {
      // Build a fresh query string (no merge with existing search)
      const params = new URLSearchParams()
      Object.entries(entriesObject || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.set(key, String(value))
        }
      })
      history.replace({ pathname: location.pathname, search: params.toString() })
    },
    [location.pathname, history]
  )

  const reset = useCallback(
    (keys) => {
      // If no specific keys provided, clear entire search
      if (!keys || (Array.isArray(keys) && keys.length === 0)) {
        history.replace({ pathname: location.pathname, search: '' })
        return
      }
      const params = new URLSearchParams(location.search)
      keys.forEach((key) => params.delete(key))
      history.replace({ pathname: location.pathname, search: params.toString() })
    },
    [location.pathname, location.search, history]
  )

  const clearMany = useCallback((keys) => {
    // Atomic clear: remove only filter keys, preserve others as-is
    const current = new URLSearchParams(location.search)
    const merged = {}
    current.forEach((value, key) => {
      if (!keys.includes(key)) {
        merged[key] = value
      }
    })
    setAll(merged)
  }, [location.search, setAll])

  return { get, set, setMany, remove, setAll, reset, clearMany }
}
